import { defineNuxtPlugin, useRuntimeConfig } from '#app';
import { captureException } from '@sentry/vue';

/**
 * Registers the auth session-management service worker (public/auth-sw.js).
 * Once active it injects a fresh Firebase ID token into same-origin HTML
 * navigation requests, letting the SSR server authenticate the user (the
 * refresh token lives in browser IndexedDB and is unavailable to the server).
 *
 * The worker is a static file served at /auth-sw.js in both dev and prod, so it
 * registers in every environment. Public Firebase web config + the Identity
 * Platform tenant are passed via the URL query string (not secret); the worker
 * reads them from its own location. It is a classic worker (uses importScripts),
 * so no `type: 'module'`.
 */
export default defineNuxtPlugin(() => {
  if (!('serviceWorker' in navigator)) {
    return;
  }

  const config = useRuntimeConfig();

  const params = new URLSearchParams({
    apiKey: config.public.firebaseApiKey ?? '',
    authDomain: config.public.firebaseAuthDomain ?? '',
    projectId: config.public.firebaseProjectId ?? '',
    appId: config.public.firebaseAppId ?? '',
  });

  if (config.public.identityPlatformTenantId) {
    params.set('tenantId', config.public.identityPlatformTenantId);
  }

  navigator.serviceWorker
    .register(`/auth-sw.js?${params.toString()}`, {
      scope: '/',
    })
    .catch((err) => {
      captureException(err, {
        level: 'warning',
        tags: { feature: 'auth-service-worker' },
      });
    });
});
